/** @jsx jsx */
import { FC, ReactNode, useRef, Fragment, useEffect } from "react";
import { jsx } from "@emotion/react";
import { v4 } from "uuid";
import { InputAdornment, TextField } from "@material-ui/core";
import { noop } from "lodash";
import {
  adornmentOverrideCss,
  ClearIcon,
  HTMLTooltip,
  inputFieldCss,
  MoreIcon,
  SearchIcon,
  TooltipContentContainer,
} from "./styles";
import { EditRowsWithColumns } from "../../../../requests/tableRowActions/types";
import Loader from "../../Loader";
import { TableCell } from "../../../../requests/table/types";
import { getStringifiedTableRows } from "./utils";

type TooltipContentRowParams = {
  cell: TableCell;
  index: number;
};

export interface RelationInputProps {
  name: string | undefined;
  rowData: EditRowsWithColumns;
  disabled: boolean;
  toggleModal: () => void;
  isLoading: boolean;
  required: boolean;
  value: number | string | null;
  modalIsOpen: boolean;
  clearData(e: React.MouseEvent<Element>): void;
  onChange(e: React.ChangeEvent<HTMLInputElement>): void;
  searchText: string;
  clearSearchText: () => void;
}

const RelationInput: FC<RelationInputProps> = ({
  name,
  rowData,
  toggleModal,
  disabled,
  isLoading,
  required,
  value,
  modalIsOpen,
  clearData,
  onChange,
  searchText,
  clearSearchText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const hasError = required && !value;
  const inputValueString = getStringifiedTableRows(rowData);
  const css = inputFieldCss(disabled, hasError);

  const isValue = searchText || inputValueString;

  const getTooltipContentRow = ({
    cell: { columnName, value: tooltipValue },
    index,
  }: TooltipContentRowParams): ReactNode => (
    <span key={columnName}>
      <strong>{`${rowData[index].columns[columnName]?.displayName}:`}</strong>
      {` ${tooltipValue}`}
    </span>
  );

  const getTooltipContent = (): JSX.Element => (
    <TooltipContentContainer>
      {rowData.map((rowWithCols, index) =>
        Object.values(rowWithCols?.row?.cells || {}).map((cell) =>
          getTooltipContentRow({ cell, index })
        )
      )}
    </TooltipContentContainer>
  );

  const getAdornmentContent = (): JSX.Element => {
    if (!rowData[0]) return <SearchIcon fontSize="small" />;
    return (
      <Fragment>
        {!disabled && (
          <ClearIcon
            data-cy="clear-icon-VzP"
            style={{
              visibility: isValue ? "visible" : "hidden",
            }}
            onClick={(e) => {
              clearSearchText();
              clearData(e);
              if (inputRef.current) inputRef.current.focus();
            }}
          />
        )}
        <HTMLTooltip
          title={getTooltipContent()}
          placement="top-end"
          active={!disabled}
        >
          {disabled ? (
            <MoreIcon
              data-cy="more-icon-VzP"
              fontSize="small"
              onClick={!disabled ? toggleModal : () => null}
              active={!disabled}
            />
          ) : (
            <SearchIcon
              data-cy="search-icon-VzP"
              fontSize="small"
              onClick={!disabled ? toggleModal : () => null}
              active={!disabled}
            />
          )}
        </HTMLTooltip>
      </Fragment>
    );
  };

  const getInputAdornment = (): ReactNode => (
    <InputAdornment position="end" css={adornmentOverrideCss}>
      {getAdornmentContent()}
    </InputAdornment>
  );

  const handleClick = (): void => {
    if (!disabled) inputRef.current?.focus();
  };

  const keyboardHandler = (e: KeyboardEvent): void => {
    if (e.key === "Enter" && !modalIsOpen) {
      toggleModal();
      inputRef.current?.blur();
    }
  };

  const inputProps = {
    endAdornment: getInputAdornment(),
    disabled,
  };

  if (isLoading) {
    return <Loader size={24} />;
  }

  return (
    <TextField
      key={name}
      css={css}
      id={name}
      type="search"
      name={name}
      value={inputValueString || searchText}
      onClick={handleClick}
      onChange={onChange}
      onKeyDown={keyboardHandler as unknown as (e: React.KeyboardEvent) => void}
      disabled={disabled}
      required={required}
      inputRef={inputRef}
      InputProps={inputProps}
    />
  );
};

export default RelationInput;
